import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { InvoiceListParams } from "@/lib/invoice-list-params";

interface InvoiceFiltersProps {
  params: InvoiceListParams;
  onChange: (patch: Partial<InvoiceListParams>) => void;
}

const set = "border-foreground font-medium";

/** Søk, periode, status og type på /faktura (VIB-2948). Søkefeltet får fokus med «/». */
export const InvoiceFilters = forwardRef<HTMLInputElement, InvoiceFiltersProps>(function InvoiceFilters(
  { params, onChange },
  searchRef,
) {
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
      <Input
        ref={searchRef}
        id="invoice-search"
        type="search"
        value={params.q}
        onChange={(e) => onChange({ q: e.target.value })}
        placeholder="Søk fakturanr, kunde eller prosjekt (/)"
        aria-label="Søk i fakturaer"
        className="w-full sm:w-80"
      />
      <Select value={params.period} onValueChange={(v) => onChange({ period: v as InvoiceListParams["period"] })}>
        <SelectTrigger aria-label="Periode" className={cn("w-full sm:w-44", params.period !== "ytd" && set)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ytd">I år</SelectItem>
          <SelectItem value="last_year">I fjor</SelectItem>
          <SelectItem value="last_12m">Siste 12 mnd</SelectItem>
          <SelectItem value="all">Alle perioder</SelectItem>
          <SelectItem value="custom">Egendefinert</SelectItem>
        </SelectContent>
      </Select>
      {params.period === "custom" && (
        <div className="flex gap-2 items-center">
          <Input
            id="invoice-from"
            type="date"
            value={params.from ?? ""}
            onChange={(e) => onChange({ from: e.target.value || null })}
            aria-label="Fakturadato fra"
            className="w-40"
          />
          <span className="text-muted-foreground">–</span>
          <Input
            id="invoice-to"
            type="date"
            value={params.to ?? ""}
            onChange={(e) => onChange({ to: e.target.value || null })}
            aria-label="Fakturadato til"
            className="w-40"
          />
        </div>
      )}
      <Select value={params.status} onValueChange={(v) => onChange({ status: v as InvoiceListParams["status"] })}>
        <SelectTrigger aria-label="Status" className={cn("w-full sm:w-44", params.status !== "all" && set)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle statuser</SelectItem>
          <SelectItem value="outstanding">Utestående</SelectItem>
          <SelectItem value="draft">Utkast</SelectItem>
          <SelectItem value="sent">Sendt</SelectItem>
          <SelectItem value="overdue">Forfalt</SelectItem>
          <SelectItem value="paid">Betalt</SelectItem>
          <SelectItem value="credited">Kreditert</SelectItem>
          <SelectItem value="credit_note">Kreditnota</SelectItem>
        </SelectContent>
      </Select>
      <Select value={params.type} onValueChange={(v) => onChange({ type: v as InvoiceListParams["type"] })}>
        <SelectTrigger aria-label="Type" className={cn("w-full sm:w-44", params.type !== "all" && set)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle typer</SelectItem>
          <SelectItem value="startup">Oppstart</SelectItem>
          <SelectItem value="interim">Mellomfaktura</SelectItem>
          <SelectItem value="final">Slutt</SelectItem>
          <SelectItem value="upsell">Mersalg</SelectItem>
          <SelectItem value="credit_note">Kreditnota</SelectItem>
          <SelectItem value="poweroffice">Fra PowerOffice</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
});
